import { faLock, faTrashCan, faX } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ButtonType } from "components/Button/Button";
import { ModalButtons } from "components/Modal/ModalButtons";
import { useModal } from "components/Modal/context/ModalContext";
import "components/Toast.scss";
import { PackageType, getPackageTypeString } from "features/Shared/ContentData/PackageType";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressBar } from "primereact/progressbar";
import { Toast } from "primereact/toast";
import { useCallback, useEffect, useRef, useState } from "react";
import { formatDate, formatFileSize } from "utils/formatting";
import { useContentUpload } from "../../Uploading/hooks/useContentUpload";
import { useSearchTagFetch } from "../../Uploading/hooks/useSearchTagFetch";
import { UploadingStateType, useUploadInProgress } from "../../Uploading/hooks/useUploadInProgress";
import { useEditMode } from "../../hooks/useEditMode";
import { EDIT_FIELD, EDIT_SCOPE, MODULE_DESCRIPTION_ROWS } from "../../utils/constants";
import {
    handleImageFileChange,
    handleMultilineInputKeyDown,
    handleSingleLineInputKeyDown,
    hasBeenEdited,
	openFilePicker,
	autoFocusRef
} from "../../utils/uiInteractionHelpers";
import ClickBoundary from "../ClickBoundary/ClickBoundary";
import EditButtonGroup from "../EditButton/EditButtonGroup";
import SortOrder from "../SortOrder/SortOrder";
import "./SubmoduleEditor.scss";

const KEYWORDS_FIELD = "Keywords";

// Missed-push safety net: unlock Keywords after this long even if no SearchTagsReady message arrives.
const SEARCH_TAGS_FALLBACK_TIMEOUT_MS = 20 * 60 * 1000;

const SubmoduleEditor = ({
	canEdit,
	moduleId,
	deviceTraining,
	submodule, // published
	currentDraft,
	index,
	total,
	onMove,
	onMoveTop,
	onMoveBottom,
	onChange,
	onDelete,
	onUpdateSubmoduleImageFile,
	draftId
}) => {
	const { setEditField, isEditing, cancelEditMode } = useEditMode();
	const { openModal } = useModal();
	
	const [latestDraft, setLatestDraft] = useState({
		Name: "",
		Description: "",
		Keywords: "",
		Duration: "",
		Content: {
			StoredName: "",
			UploadDate: "",
			FileSize: 0,
			PackageType: PackageType.Invalid,
			LaunchPage: "",
			JobId: ""
		},
		ImageUrl: "",
		ImageFile: null,
		HasProgress: true
	});
	const latestDraftRef = useRef(latestDraft);
	const [showContentDetails, setShowContentDetails] = useState(false);
	// Local, per-submodule flag: true only in the SubmoduleEditor that started the current upload.
	const [isUploadingHere, setIsUploadingHere] = useState(false);
	// StoredName whose AI search tags are still being generated (batch transcription finishes minutes after the upload completes); locks this submodule's Keywords until resolved.
	const [awaitingSearchTags, setAwaitingSearchTags] = useState(null);
	const searchTagsFallbackTimerRef = useRef(null);
	const { uploadZipFile, cancelUpload, uploadFilename, uploadProgressValue, clearProgress } = useContentUpload();
	const { uploadingState, setUploadingState, processComplete, setProcessComplete, searchTagsReady, setSearchTagsReady } = useUploadInProgress();
	const { fetchSearchTags } = useSearchTagFetch();
	const contentFileInputRef = useRef(null);
	const contentFileDataRef = useRef(null);
	const deviceFileInputRef = useRef(null);
	const toast = useRef(null);

	const initialiseSubmoduleDraftData = useCallback((draft, submodule) => {
		setLatestDraft({
			Name: draft?.[EDIT_FIELD.NAME] ?? submodule.Name ?? "",
			Description: draft?.[EDIT_FIELD.DESCRIPTION] ?? submodule.Description ?? "",
			Keywords: draft?.Keywords ?? submodule.Keywords ?? "",
			Duration: draft?.Duration ?? submodule.Duration ?? "",
			Content: {
				StoredName: draft?.StoredName ?? submodule.StoredName ?? "",
				OriginalName: draft?.OriginalName ?? submodule.OriginalName ?? "",
				UploadDate: draft?.UploadDate ?? submodule.UploadDate ?? "",
				FileSize: draft?.FileSize ?? submodule.FileSize ?? 0,
				PackageType: draft?.PackageType ?? submodule.PackageType ?? PackageType.Invalid,
				LaunchPage: draft?.LaunchPage ?? submodule.LaunchPage ?? "",
				JobId: draft?.JobId ?? ""
			},
			ImageUrl: draft?.ImageUrl || submodule.ImageUrl || "",
			HasProgress: draft?.HasProgress !== undefined ? Boolean(draft.HasProgress) : Boolean(submodule?.HasProgress)
		});
	}, []);

	// Hook to initialise input field information for editing.
	useEffect(() => {
		if (currentDraft || submodule) {
			initialiseSubmoduleDraftData(currentDraft, submodule);
		}
	}, [initialiseSubmoduleDraftData, currentDraft, submodule]);

	useEffect(() => {
		latestDraftRef.current = latestDraft;
	}, [latestDraft]);
	
	/**
	 * Required fields within SubmoduleDataDto
	 * Id (int)
	 * Name (string)
	 * Description (string)
	 * Position (int)
	 * Keywords (string?)
	 * HasProgress (bool)
	 * ImageUrl (string?)
	 * StoredName (string)
	 * OriginalName (string)
	 * UploadDate (string)
	 * FileSize (int)
	 * PackageType (int - PackageType enum)
	 * LaunchPage (string?)
	 * JobId (string?)
	 */
	const getSubmoduleDtoDraft = (overrides = {}) => ({
		...submodule,
		[EDIT_FIELD.NAME]: latestDraft.Name,
		[EDIT_FIELD.DESCRIPTION]: latestDraft.Description,
		Keywords: latestDraft.Keywords,
		Duration: latestDraft.Duration,
		[EDIT_FIELD.IMAGE_URL]: latestDraft.ImageUrl,
		HasProgress: latestDraft.HasProgress,
		StoredName: latestDraft.Content.StoredName,
		OriginalName: latestDraft.Content.OriginalName,
		UploadDate: latestDraft.Content.UploadDate,
		FileSize: latestDraft.Content.FileSize,
		PackageType: latestDraft.Content.PackageType,
		LaunchPage: latestDraft.Content.LaunchPage,
		JobId: latestDraft.Content.JobId,
		...overrides
	});

	const handleTextUpdate = (override) => {
		const draftUpdate = {
			...latestDraft,
			...override
		}
		setLatestDraft(draftUpdate);
		onChange(getSubmoduleDtoDraft(override));
	}

	const handleHasProgress = () => {
		const newValue = !latestDraft.HasProgress;
		const draftUpdate = {
			...latestDraft,
			HasProgress: newValue
		}
		setLatestDraft(draftUpdate);
		onChange(getSubmoduleDtoDraft({ HasProgress: newValue }));
	};

	const onZipFileUploadSuccess = () => {
		setUploadingState(UploadingStateType.Validating);
	};

	const onZipFileProcessSuccess = (data, jobId) => {
		setUploadingState(UploadingStateType.Processing);

		const fileSize = contentFileDataRef.current.size;
		const originalFileName = contentFileDataRef.current.name;
		contentFileDataRef.current = null;

		const validateData = data.packageValidation;
		const contentOverrides = {
			StoredName: data.proposedContainer,
			OriginalName: originalFileName,
			PackageType: validateData.Type,
			FileSize: fileSize,
			UploadDate: validateData.UploadDate,
			LaunchPage: validateData.LaunchPage,
			JobId: jobId
		};

		setLatestDraft((current) => ({
			...current,
			Content: {
				StoredName: data.proposedContainer,
				OriginalName: originalFileName,
				PackageType: validateData.Type,
				FileSize: fileSize,
				UploadDate: validateData.UploadDate,
				LaunchPage: validateData.LaunchPage,
				JobId: jobId
			}
		}));

		// Use the ref to get current draft values (not stale closure).
		const currentDraftSnapshot = latestDraftRef.current;
		onChange({
			...submodule,
			[EDIT_FIELD.NAME]: currentDraftSnapshot.Name,
			[EDIT_FIELD.DESCRIPTION]: currentDraftSnapshot.Description,
			Keywords: currentDraftSnapshot.Keywords,
			Duration: currentDraftSnapshot.Duration,
			[EDIT_FIELD.IMAGE_URL]: currentDraftSnapshot.ImageUrl,
			HasProgress: currentDraftSnapshot.HasProgress,
			...contentOverrides
		});
	};

	const onZipFileError = (error) => {
		contentFileDataRef.current = null;
		toast.current?.show({
			severity: "error",
			summary: "Cannot upload Content!",
			detail: error,
			sticky: true
		});
		setIsUploadingHere(false);
		setUploadingState(UploadingStateType.None);
	}

	// Content uploads need a real DraftId. The upload controls are only reachable while the course is
	// locked for editing, and locking (or reloading a locked course) always yields a DraftId via the
	// shared DataFetcher, so the draftId prop is populated by the time an upload can start. The guard
	// below is defensive only: if it were somehow missing, block rather than post to ".../null".
	const beginUpload = () => {
		if (!draftId) {
			onZipFileError("The course must be locked for editing before uploading. Please try again.");
			return;
		}
		// The Keywords/ Search Tags are auto-generated and overwritten when processing completes, so lock the field for
		// this submodule while its upload runs. Drop out of an in-progress Keywords edit first.
		if (isEditing(EDIT_SCOPE.SUBMODULE, KEYWORDS_FIELD, submodule.Id, moduleId)) {
			cancelEditMode();
		}		
		if (latestDraft.Keywords) {
			applyGeneratedKeywords("");
		}
		// A new upload supersedes any tags still pending from the previous one.
		setAwaitingSearchTags(null);
		setIsUploadingHere(true);
		setUploadingState(UploadingStateType.Uploading);
		const existingDraft = (latestDraft.Content.StoredName && latestDraft.Content.JobId) ? latestDraft.Content.StoredName : null;
		uploadZipFile(existingDraft, contentFileDataRef.current, draftId, onZipFileUploadSuccess, onZipFileProcessSuccess, onZipFileError);
	};

	const handleConfirmUploadLargeZipFile = (yes) => {
		if (yes) {
			beginUpload();
		}
		else {
			contentFileDataRef.current = null;
		}
	};

	const handleFileChange = async (e) => {
		if (contentFileDataRef.current) {
			return;
		}

		contentFileDataRef.current = e.target.files && e.target.files[0];
		if (!contentFileDataRef.current) {
			return;
		}
		if (contentFileDataRef.current.size > 500 * 1024 * 1024) {
			openModal(
				null,
				<div>
					This file is over 500Mb in size.
					<br/>
					If you have not done so already, you may be able to compress the videos to bring the total file size below our limit.
				</div>,
				ModalButtons.Ok,
				() => {
					clearProgress();
					contentFileDataRef.current = null;
				}
			);
			return;
		}
		if (contentFileDataRef.current.size > 150 * 1024 * 1024) {
			openModal(
				null,
				<div>
					This file is over 150Mb in size.If you have not done so already, you may need to compress the videos.
					<br />
					Are you sure you want to continue?
				</div>,
				ModalButtons.YesNo,
				handleConfirmUploadLargeZipFile
			);
			return;
		}
		beginUpload();
	};

	const handleCancelUpload = () => {
		cancelUpload();
	};

	// Applies AI-generated keywords
	const applyGeneratedKeywords = (tags) => {
		setLatestDraft((current) => ({
			...current,
			Keywords: tags
		}));

		const currentDraftSnapshot = latestDraftRef.current;
		onChange({
			...submodule,
			[EDIT_FIELD.NAME]: currentDraftSnapshot.Name,
			[EDIT_FIELD.DESCRIPTION]: currentDraftSnapshot.Description,
			Keywords: tags,
			Duration: currentDraftSnapshot.Duration,
			[EDIT_FIELD.IMAGE_URL]: currentDraftSnapshot.ImageUrl,
			HasProgress: currentDraftSnapshot.HasProgress,
			StoredName: currentDraftSnapshot.Content.StoredName,
			OriginalName: currentDraftSnapshot.Content.OriginalName,
			UploadDate: currentDraftSnapshot.Content.UploadDate,
			FileSize: currentDraftSnapshot.Content.FileSize,
			PackageType: currentDraftSnapshot.Content.PackageType,
			LaunchPage: currentDraftSnapshot.Content.LaunchPage,
			JobId: currentDraftSnapshot.Content.JobId
		});
	};

	useEffect(() => {
		if (processComplete && uploadingState !== UploadingStateType.None) {
			toast.current?.show({
				severity: processComplete.success ? "success" : "error",
				summary: processComplete.success ? "Processing complete" : "Cannot process Content!",
				detail: processComplete.error || "Content processed successfully!",
				sticky: processComplete.error
			});

			// The search tags arrive asynchronously (batch transcription), so lock Keywords until the
			// SearchTagsReady push resolves the outcome. The immediate fetch covers the synchronous
			// paths where the tags were already persisted before this completion message.
			if (processComplete.success && isUploadingHere) {
				const uploadedStoredName = latestDraftRef.current.Content.StoredName;
				setAwaitingSearchTags(uploadedStoredName);
				fetchSearchTags(uploadedStoredName, (tags) => {
					// Re-check at apply time: a new upload may have replaced the container while the fetch ran.
					if (uploadedStoredName === latestDraftRef.current.Content.StoredName) {
						applyGeneratedKeywords(tags);
					}
					setAwaitingSearchTags((current) => (current === uploadedStoredName ? null : current));
				});
			}

			setProcessComplete(null);
			setIsUploadingHere(false);
			setUploadingState(UploadingStateType.None);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [processComplete, setProcessComplete, uploadingState, setUploadingState, isUploadingHere, fetchSearchTags]);

	// Resolves the Keywords lock when the backend announces this upload's search tag outcome. The context
	// retains the message, so this works whether the push arrives before or after the completion above.
	// A push landing after the fallback below has already unlocked the field (a job can legitimately run
	// longer than the fallback allows) is still honoured: the tags are applied as long as Keywords is
	// still empty, so they never clobber anything typed after the unlock.
	useEffect(() => {
		if (!searchTagsReady) {
			return;
		}
		const payload = searchTagsReady.payload ?? searchTagsReady.Payload ?? {};
		const storedName = payload.storedName ?? payload.StoredName ?? "";
		if (!storedName) {
			return;
		}
		// A StoredName is unique to one upload, so exactly one submodule matches and consumes the message.
		const isAwaitedHere = storedName === awaitingSearchTags;
		const isCurrentContentHere = storedName === latestDraftRef.current.Content.StoredName;
		if (!isAwaitedHere && !isCurrentContentHere) {
			return;
		}
		setSearchTagsReady(null);
		const hasTags = payload.hasTags ?? payload.HasTags ?? false;
		if (!hasTags) {
			// No tags are coming (no transcript, generation produced nothing, or the batch job failed).
			setAwaitingSearchTags((current) => (current === storedName ? null : current));
			return;
		}
		fetchSearchTags(storedName, (tags) => {
			const keywordsUntouched = isAwaitedHere || !latestDraftRef.current.Keywords;
			if (storedName === latestDraftRef.current.Content.StoredName && keywordsUntouched) {
				applyGeneratedKeywords(tags);
			}
			setAwaitingSearchTags((current) => (current === storedName ? null : current));
		});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchTagsReady, awaitingSearchTags, setSearchTagsReady, fetchSearchTags]);

	// Missed-push safety net: if no SearchTagsReady message resolves the lock (SignalR reconnect, message
	// lost), make one final fetch and unlock regardless, so the field can never stay locked indefinitely.
	useEffect(() => {
		if (!awaitingSearchTags) {
			return;
		}
		const storedName = awaitingSearchTags;
		searchTagsFallbackTimerRef.current = setTimeout(() => {
			fetchSearchTags(storedName, (tags) => {
				if (storedName === latestDraftRef.current.Content.StoredName) {
					applyGeneratedKeywords(tags);
				}
			});
			setAwaitingSearchTags((current) => (current === storedName ? null : current));
		}, SEARCH_TAGS_FALLBACK_TIMEOUT_MS);
		return () => clearTimeout(searchTagsFallbackTimerRef.current);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [awaitingSearchTags, fetchSearchTags]);

	const handleCommitImageFile = (fileData) => {
		const draftUpdate = {
			...latestDraft,
			ImageFile: fileData
		}
		setLatestDraft(draftUpdate);
		onUpdateSubmoduleImageFile({
			id: submodule.Id,
			data: fileData
		});
		onChange(getSubmoduleDtoDraft());
	};
	
	const fieldGetters = {
		[EDIT_FIELD.NAME]: () => latestDraft.Name,
		[EDIT_FIELD.DESCRIPTION]: () => latestDraft.Description,
		Keywords: () => latestDraft.Keywords,
		Duration: () => latestDraft.Duration,
		[EDIT_FIELD.IMAGE_URL]: () => latestDraft.ImageUrl
	};

	const setSubmoduleEditField = (field) => {
		setEditField({
			scope: EDIT_SCOPE.SUBMODULE,
			id: submodule.Id,
			parentId: moduleId,
			field: field
		});
	};

	// Lock this submodule's Keywords while its own upload is processing: the generated search tags overwrite
	// the field on completion, so editing here would be lost. Scoped to this submodule via the local flag.
	const isUploadProcessing = uploadingState !== UploadingStateType.None && isUploadingHere;
	// Also lock when no content has ever been uploaded: keywords are generated from the content, so there
	// is nothing meaningful to edit until an upload has completed.
	const isAwaitingContent = !latestDraft.Content.StoredName;
	// And keep the lock while the AI search tags for this submodule's upload are still being generated
	// (batch transcription completes minutes after the upload itself).
	const isAwaitingSearchTags = awaitingSearchTags !== null;
	const isKeywordsLocked = isUploadProcessing || isAwaitingContent || isAwaitingSearchTags;
	const keywordsLockedMessage = isUploadProcessing
		? "Locked while content is processing"
		: isAwaitingSearchTags
			? "Locked while search tags are generated"
			: "Locked pending content upload";

	return (
		<div className="editable-submodule">
			<div className="editable-submodule-sidebar">
				<SortOrder
					index={index}
					total={total}
					onMove={onMove}
					onMoveTop={onMoveTop}
					onMoveBottom={onMoveBottom}
					label="Submodule Position"
				/>
				<div className="editable-module-sidebar-delete" onClick={() => onDelete(submodule.Id)}>
					<FontAwesomeIcon icon={faTrashCan} />
					<p>
						Delete
						<br />
						Submodule
					</p>
				</div>
			</div>

			{/* Submodule Name */}
			<div className="editable-submodule-main">
				<div className="editable-submodule-header">
					<ClickBoundary>
						{isEditing(EDIT_SCOPE.SUBMODULE, EDIT_FIELD.NAME, submodule.Id, moduleId) ? (
							<InputText
								ref={autoFocusRef}
								maxLength={150}
								value={latestDraft.Name}
								onChange={(e) => setLatestDraft((d) => ({ ...d, [EDIT_FIELD.NAME]: e.target.value }))}
								onKeyDown={(e) => handleSingleLineInputKeyDown(e, cancelEditMode)}
								onBlur={() => handleTextUpdate({ [EDIT_FIELD.NAME]: latestDraft.Name })}
								className="title-textbox"
							/>
						) : (
							<div className="editable-submodule-title">
								<h2>{latestDraft.Name || "Add Name"}</h2>
								<EditButtonGroup
									show={canEdit}
									onEdit={() => setSubmoduleEditField(EDIT_FIELD.NAME)}
									onUndo={() => handleTextUpdate({ [EDIT_FIELD.NAME]: submodule.Name })}
									showUndo={hasBeenEdited(submodule, fieldGetters, EDIT_FIELD.NAME)}
								/>
							</div>
						)}
					</ClickBoundary>
				</div>

				{/* Submodule Description */}
				<div className="editable-submodule-row">
					<ClickBoundary>
						{isEditing(EDIT_SCOPE.SUBMODULE, EDIT_FIELD.DESCRIPTION, submodule.Id, moduleId) ? (
							<InputTextarea
								ref={autoFocusRef}
								maxLength={3000}
								value={latestDraft.Description}
								onChange={(e) => setLatestDraft((d) => ({ ...d, [EDIT_FIELD.DESCRIPTION]: e.target.value }))}
								onKeyDown={(e) => handleMultilineInputKeyDown(e, cancelEditMode)}
								onBlur={() => handleTextUpdate({ [EDIT_FIELD.DESCRIPTION]: latestDraft.Description })}
								rows={MODULE_DESCRIPTION_ROWS}
								autoResize
								className="description-textbox"
							/>
						) : (
							<div className="editable-submodule-main-description">
								<p>{latestDraft.Description || "Add Description"}</p>
								<EditButtonGroup
									show={canEdit}
									onEdit={() => setSubmoduleEditField(EDIT_FIELD.DESCRIPTION)}
									onUndo={() => handleTextUpdate({ [EDIT_FIELD.DESCRIPTION]: submodule.Description })}
									showUndo={hasBeenEdited(submodule, fieldGetters, EDIT_FIELD.DESCRIPTION)}
								/>
							</div>
						)}
					</ClickBoundary>
				</div>

				{/* Keywords */}
				<div className="editable-submodule-row">
					<h4>Keywords</h4>
					<ClickBoundary>
						{isEditing(EDIT_SCOPE.SUBMODULE, KEYWORDS_FIELD, submodule.Id, moduleId) && !isKeywordsLocked ? (
							<InputTextarea
								ref={autoFocusRef}
								maxLength={3000}
								value={latestDraft.Keywords}
								onChange={(e) => setLatestDraft((d) => ({ ...d, Keywords: e.target.value }))}
								onKeyDown={(e) => handleMultilineInputKeyDown(e, cancelEditMode)}
								onBlur={() => handleTextUpdate({ Keywords: latestDraft.Keywords })}
								rows={MODULE_DESCRIPTION_ROWS}
								autoResize
								className="field-textbox"
								placeholder="e.g. Adding, Users, Admin"
							/>
						) : (
							<div className="field-value">
								<p>{latestDraft.Keywords || "Add keywords"}</p>
								{isKeywordsLocked ? (
									<span className="keywords-locked">
										<FontAwesomeIcon icon={faLock} /> {keywordsLockedMessage}
									</span>
								) : (
									<EditButtonGroup
										show={canEdit}
										onEdit={() => setSubmoduleEditField(KEYWORDS_FIELD)}
										onUndo={() => handleTextUpdate({ Keywords: submodule.Keywords })}
										showUndo={hasBeenEdited(submodule, fieldGetters, KEYWORDS_FIELD)}
									/>
								)}
							</div>
						)}
					</ClickBoundary>
				</div>

				{/* Show Progress */}
				<div className="editable-submodule-row">
					<h4>Show Progress</h4>
					<div className="field-value">
						<label className="checkbox-inline">
							<input
								type="checkbox"
								checked={latestDraft.HasProgress}
								onChange={handleHasProgress}
								className="editable-submodule-row-checkbox"
							/>
						</label>
					</div>
				</div>

				{/* Content */}
				<div className="editable-submodule-row">
					<h4>Content</h4>
					<ClickBoundary>
						<div className="content-upload">
							<p>{latestDraft.Content.StoredName || "None"}</p>
							<div className="content-upload-buttons">
								<div
									className={`content-upload-buttons-upload${uploadingState !== UploadingStateType.None ? " disabled" : ""}`}
									onClick={() => openFilePicker(contentFileInputRef)}
								>
									<p>Upload +</p>
									<input
										type="file"
										accept=".zip"
										ref={contentFileInputRef}
										style={{ display: "none" }}
										onChange={handleFileChange}
									/>
								</div>
								{latestDraft.Content.StoredName && (
									<div className="content-upload-buttons-details" onClick={() => setShowContentDetails((prev) => !prev)}>
										<p>{showContentDetails ? "Hide Details" : "Show Details"}</p>
									</div>
								)}
							</div>

							{/* Details */}
							{showContentDetails && (
								<div className="content-upload-details">
									<div className="content-upload-details-detail">
										<h4>Package Type</h4>
										<p>{getPackageTypeString(latestDraft.Content.PackageType)}</p>
									</div>
									<div className="content-upload-details-detail">
										<h4>Upload Date</h4>
										<p>{formatDate(latestDraft.Content.UploadDate, true)}</p>
									</div>
									<div className="content-upload-details-detail">
										<h4>File Size</h4>
										<p>{formatFileSize(latestDraft.Content.FileSize)}</p>
									</div>
								</div>
							)}
						</div>
					</ClickBoundary>
				</div>

				{/* Device Image */}
				{deviceTraining && (
					<div className="editable-submodule-row">
						<h4>Device Image</h4>
						<div className="editable-submodule-row-device">
							<div className="editable-submodule-row-device-image">
								{(latestDraft.ImageUrl ?? "").length > 0
									? <img src={latestDraft.ImageUrl} alt={latestDraft.Name} className="editable-submodule-row-device-image-img" />
									: <p>Add Device Image</p>
								}
							</div>
							<div className="editable-submodule-row-device-image-upload">
								<input
									ref={deviceFileInputRef}
									type="file"
									accept="image/*"
									style={{ display: "none" }}
									onChange={(e) => handleImageFileChange(e, deviceFileInputRef, openModal, handleCommitImageFile)}
								/>
								<EditButtonGroup
									static
									editText="Edit Image"
									show={canEdit}
									onEdit={() => openFilePicker(deviceFileInputRef)}
									onUndo={() => handleTextUpdate({ [EDIT_FIELD.IMAGE_URL]: submodule.ImageUrl })}
									showUndo={hasBeenEdited(submodule, fieldGetters, EDIT_FIELD.IMAGE_URL)}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
			{/* Upload Progress */}
			{uploadingState !== UploadingStateType.None && (
				<div className="upload-submodule-content">
					<h4>{uploadingState}...</h4>
					<p>{uploadFilename}</p>
					<ProgressBar
						className="upload-submodule-progressbar"
						mode={uploadingState === UploadingStateType.Uploading ? "determinate" : "indeterminate"}
						value={uploadProgressValue}
					/>
					{uploadingState === UploadingStateType.Uploading && (
						<Button type={ButtonType.Tertiary} text="Cancel" onClick={handleCancelUpload} icon={faX} flipped />
					)}
				</div>
			)}
			<Toast ref={toast} />
		</div>
	);
};

export default SubmoduleEditor;